import cn from 'classnames';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, useParams, useSearchParams } from 'react-router';

import {
  isFollowUpDocument,
  isLiteratureReviewDocument,
} from '@/shared/api/transcriptor/transcriptorApi';
import { evaluationMessages, toastErrors } from '@/shared/config';
import {
  useAppDispatch,
  useAppSelector,
  useMedia,
  useUserTimezone,
} from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { MobileLayout, Spinner, toastCaller } from '@/shared/ui';

import { DoctorLabel } from '@/entities/doctors';
import { patientModel } from '@/entities/patient';
import {
  getConsultationDocuments,
  getRecordingPath,
  getDocumentTabLabel,
  getDocumentTabValue,
  getTranscriptorErrorMessage,
  isUploadNotCompleted,
  transcriptorModel,
} from '@/entities/transcriptor';
import { userModel } from '@/entities/user';

import {
  AddDocumentsButton,
  ConsultationOptionsPopover,
} from '@/features/consultationOptions';
import { Filters } from '@/features/filters';
import { usePatientProfileStream } from '@/features/patientProfileStream';

import { MobileHeader } from '@/widgets/Header';

import { dentalConsultationMessages, FALLBACK_TAB_VALUE } from '../../config';
import {
  useConsultationAuthorName,
  useConsultationPagePolling,
} from '../../hooks';
import { isDocumentGenerating } from '../../lib';
import { ConsultationEmptyState } from '../ConsultationEmptyState';
import { ContinueRecordingButton } from '../ContinueRecordingButton/ContinueRecordingButton';
import { EvaluationWidget } from '../EvaluationWidget/EvaluationWidget';
import { FollowUpWidget } from '../FollowUpWidget/FollowUpWidget';
import { GenericDocumentWidget } from '../GenericDocumentWidget/GenericDocumentWidget';
import { InterruptedRecordingNotice } from '../InterruptedRecordingNotice';
import { LiteratureReviewWidget } from '../LiteratureReviewWidget/LiteratureReviewWidget';
import { NoSpeechNotice } from '../NoSpeechNotice';
import { SummaryWidget } from '../SummaryWidget/SummaryWidget';
import { TabLabel } from '../TabLabel/TabLabel';
import { TranscriptionWidget } from '../TranscriptionWidget/TranscriptionWidget';

import styles from './DentalConsultation.module.scss';

export const DentalConsultation = () => {
  const { isMobile } = useMedia();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatDate, formatMessage } = intl;
  const userTimezone = useUserTimezone();
  const { patientID = '' } = useParams();

  usePatientProfileStream(patientID);

  const consultationId = searchParams.get('id');
  const patient = useAppSelector((state) =>
    patientModel.selectors.selectPatientByID(patientID)(state),
  );
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const consultation = useAppSelector((state) =>
    consultationId
      ? transcriptorModel.selectors.selectConsultationByIdIncludeArchived(
          state,
          consultationId,
        )
      : null,
  );
  const loading = useAppSelector(
    transcriptorModel.selectors.selectConsultationsLoading,
  );
  const authorName = useConsultationAuthorName(consultation);

  useEffect(() => {
    window.scrollTo(0, 0);

    trackEvent({
      eventName: 'TranscriptorReportPage Viewed',
      isPageViewEvent: true,
    });
  }, []);

  useEffect(() => {
    if (consultationId) {
      dispatch(
        transcriptorModel.thunks.fetchConsultationById({ consultationId }),
      );
    }
  }, [consultationId]);

  useConsultationPagePolling({
    consultationId: consultationId ?? undefined,
    consultationStatus: consultation?.status,
  });

  const handleRegenerate = useCallback(async () => {
    if (!consultationId) {
      return;
    }

    try {
      await dispatch(
        transcriptorModel.thunks.reprocessConsultation({ consultationId }),
      ).unwrap();
      trackEvent({
        eventName: 'TranscriptorReportPageRegenerateButtonClicked',
      });
    } catch (error) {
      const refusal = getTranscriptorErrorMessage(error);

      toastCaller({
        type: 'error',
        heading: formatMessage(toastErrors.errorHeading),
        message: formatMessage(
          refusal ?? dentalConsultationMessages.regenerateFailed,
        ),
      });
    }
  }, [consultationId, formatMessage]);

  const uploadNotCompleted = isUploadNotCompleted(consultation);
  const onRegenerate = uploadNotCompleted ? undefined : handleRegenerate;

  const canViewEvaluation = Boolean(consultation?.canViewEvaluation);

  const documents = useMemo(
    () => getConsultationDocuments(consultation),
    [consultation],
  );

  const tabItems = useMemo(
    () =>
      documents.map((doc) => ({
        value: getDocumentTabValue(doc),
        label: (
          <TabLabel
            label={getDocumentTabLabel(doc)}
            isGenerating={isDocumentGenerating(doc)}
            isRestricted={doc.type === 'evaluation' && !canViewEvaluation}
          />
        ),
      })),
    [documents, canViewEvaluation],
  );

  const availableTabValues = useMemo(
    () => tabItems.map((tab) => tab.value),
    [tabItems],
  );

  const activeTab = searchParams.get('tab') || FALLBACK_TAB_VALUE;
  const resolvedActiveTab = availableTabValues.includes(activeTab)
    ? activeTab
    : availableTabValues[0] ?? FALLBACK_TAB_VALUE;

  useEffect(() => {
    if (availableTabValues.length === 0) {
      return;
    }
    if (!availableTabValues.includes(activeTab)) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', availableTabValues[0]);
      // Replace, not push — pushing this normalization adds a history entry
      // that traps the browser Back button.
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [availableTabValues, activeTab, searchParams, setSearchParams]);

  const activeDoc = documents.find(
    (doc) => getDocumentTabValue(doc) === resolvedActiveTab,
  );

  const handleTabChange = (tab: string) => {
    const doc = documents.find((item) => getDocumentTabValue(item) === tab);

    if (doc?.type === 'summary') {
      trackEvent({ eventName: 'TranscriptorReportPageSummaryTabClicked' });
    } else if (doc?.type === 'evaluation' && canViewEvaluation) {
      trackEvent({ eventName: 'TranscriptorReportPageEvaluationTabClicked' });
    } else if (doc && isFollowUpDocument(doc)) {
      trackEvent({ eventName: 'TranscriptorReportPageFollowupTabClicked' });
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    setSearchParams(newSearchParams);
  };

  const renderActiveWidget = () => {
    if (activeDoc?.type === 'summary') {
      return (
        <SummaryWidget
          consultation={consultation}
          summary={activeDoc}
          patient={patient}
          authorName={authorName}
          userLocale={userLocale}
          onRegenerate={onRegenerate}
        />
      );
    }
    if (activeDoc?.type === 'evaluation') {
      if (!canViewEvaluation) {
        return (
          <ConsultationEmptyState
            noDataMessage={evaluationMessages.accessRestricted}
          />
        );
      }

      return (
        <EvaluationWidget
          consultation={consultation}
          evaluation={activeDoc}
          onRegenerate={onRegenerate}
        />
      );
    }
    if (activeDoc && isFollowUpDocument(activeDoc)) {
      return (
        <FollowUpWidget
          consultation={consultation}
          followUp={activeDoc}
          onRegenerate={onRegenerate}
        />
      );
    }
    if (activeDoc && isLiteratureReviewDocument(activeDoc)) {
      return (
        <LiteratureReviewWidget
          consultation={consultation}
          literatureReview={activeDoc}
          onRegenerate={onRegenerate}
        />
      );
    }
    if (activeDoc) {
      return (
        <GenericDocumentWidget
          consultation={consultation}
          document={activeDoc}
          onRegenerate={onRegenerate}
        />
      );
    }
    return null;
  };

  if (consultation?.transcriptorV2) {
    return (
      <Navigate
        replace
        to={getRecordingPath({ patientID, recordingID: consultation.id })}
      />
    );
  }

  if (loading && !consultation) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size={48} />
      </div>
    );
  }

  if (!consultation && !loading && consultationId) {
    return (
      <div className={styles.errorContainer}>
        <h2>
          {formatMessage(dentalConsultationMessages.consultationNotFound)}
        </h2>
        <p>
          {formatMessage(
            dentalConsultationMessages.consultationNotFoundDescription,
          )}
        </p>
      </div>
    );
  }

  const consultationDate = consultation?.date
    ? formatDate(consultation.date, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: userTimezone,
      })
    : '';

  const pageTitle =
    consultation?.name ||
    consultation?.schemaName ||
    formatMessage(dentalConsultationMessages.pageTitle);
  const currentLang = consultation?.lang;

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader showBackButton />
        <MobileLayout.Main>
          <div className={styles.mobileHeader}>
            <div className={styles.pageTitleRow}>
              <h1>{pageTitle}</h1>
              {consultation && (
                <ConsultationOptionsPopover
                  consultationId={consultation.id}
                  patientId={patientID}
                  isArchived={consultation.isArchived}
                  canArchive={consultation.canArchive}
                  canRestore={consultation.canRestore}
                  canDelete={consultation.canDelete}
                  recordingMode={consultation.recordingMode}
                  status={consultation.status}
                  error={consultation.error}
                />
              )}
            </div>
            <DoctorLabel
              fullName={authorName}
              avatarSrc=""
              className={styles.mobileDoctorInfo}
              size={20}
            />
            <div
              className={cn('p2', styles.mobilePatientInfo, styles.consultDate)}
            >
              {consultationDate}
            </div>
          </div>
          <NoSpeechNotice
            className={styles.noSpeechNotice}
            consultation={consultation}
          />
          <InterruptedRecordingNotice consultation={consultation} />
          <div className={styles.tabsRow}>
            <Filters
              className={styles.tabs}
              filterName="tab"
              defaultActiveKey={resolvedActiveTab}
              items={tabItems}
              onChange={handleTabChange}
            />
            {consultation && !uploadNotCompleted && (
              <AddDocumentsButton
                consultationId={consultation.id}
                schemaId={consultation.schemaId}
                documents={documents}
                recordingMode={consultation.recordingMode}
                status={consultation.status}
              />
            )}
          </div>
          <div className={styles.contentArea}>
            <Fragment key={resolvedActiveTab}>{renderActiveWidget()}</Fragment>
            {consultation &&
              activeDoc?.type === 'summary' &&
              !uploadNotCompleted && (
                <ContinueRecordingButton
                  patientId={patientID}
                  encounterId={consultation.id}
                  status={consultation.status}
                  schemaId={consultation.schemaId}
                  language={currentLang}
                />
              )}
            <TranscriptionWidget consultation={consultation} />
          </div>
        </MobileLayout.Main>
      </MobileLayout>
    );
  }

  return (
    <div className={styles.page} data-section="dental-consultation">
      <div className={styles.pageHeader}>
        <div className={styles.pageTitleRow}>
          <h1 className={styles.pageTitle}>{pageTitle}</h1>
          {consultation && (
            <ConsultationOptionsPopover
              consultationId={consultation.id}
              patientId={patientID}
              isArchived={consultation.isArchived}
              canArchive={consultation.canArchive}
              canRestore={consultation.canRestore}
              canDelete={consultation.canDelete}
              recordingMode={consultation.recordingMode}
              status={consultation.status}
              error={consultation.error}
            />
          )}
        </div>
        <div className={styles.patientInfo}>
          <DoctorLabel
            fullName={authorName}
            avatarSrc=""
            className={styles.doctorInfo}
            size={20}
          />
          <div className={cn('p2', styles.patientDetails, styles.consultDate)}>
            {consultationDate}
          </div>
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.leftColumn}>
          <NoSpeechNotice
            className={styles.noSpeechNotice}
            consultation={consultation}
          />
          <InterruptedRecordingNotice consultation={consultation} />
          <div className={styles.tabsRow}>
            <Filters
              className={styles.tabs}
              filterName="tab"
              defaultActiveKey={resolvedActiveTab}
              items={tabItems}
              onChange={handleTabChange}
            />
            {consultation && !uploadNotCompleted && (
              <AddDocumentsButton
                consultationId={consultation.id}
                schemaId={consultation.schemaId}
                documents={documents}
                recordingMode={consultation.recordingMode}
                status={consultation.status}
              />
            )}
          </div>
          <div className={styles.leftWidget}>
            <Fragment key={resolvedActiveTab}>{renderActiveWidget()}</Fragment>
            {consultation &&
              activeDoc?.type === 'summary' &&
              !uploadNotCompleted && (
                <ContinueRecordingButton
                  patientId={patientID}
                  encounterId={consultation.id}
                  status={consultation.status}
                  schemaId={consultation.schemaId}
                  language={currentLang}
                />
              )}
          </div>
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.rightWidget}>
            <TranscriptionWidget consultation={consultation} />
          </div>
        </div>
      </div>
    </div>
  );
};
